import React, { useState } from 'react';
import styles from './Modal.module.css';
import PlusIcon from '../../assets/icons/PlusIcon';
import MinusIcon from '../../assets/icons/MinusIcon';
import { topUpBalance } from '../../services/cashIn.service';
import { withdrawBalance } from '../../services/cashOut.service';
import { getErrorMessage } from '../../utils/helpers/getErrorMessage';
import { UserRole, UserById } from '../../services/getUserById.service';
import { authService } from '../../services/auth.service';

interface CashInOutFormProps {
  mode: 'in' | 'out';
  balance: number;
  userId: number;
  role: UserRole;
  user?: UserById; // Add user prop to get balance and totalActiveBonus
  onSave: (amount: number, wager: boolean, comment: string) => void;
  onCancel: () => void;
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
}

const CashInOutForm: React.FC<CashInOutFormProps> = ({
  mode,
  balance,
  userId,
  role,
  user,
  onSave,
  onCancel,
  onSuccess,
  onError,
}) => {
  const [amount, setAmount] = useState<number | ''>('');
  const [wager, setWager] = useState(false);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isChashoutable = (user?.Balance?.onHold && user?.Balance?.onHold > 0 && role?.name === 'USER' ) || false;

  // Dynamically set presetAmounts based on role
  const presetAmounts =
    role?.name === 'USER' ? [10, 20, 50, 100] : [1000, 2000, 5000, 10000];

  // Calculate available balance for cash out for USER type players
  const availableBalance = React.useMemo(() => {
    if (mode === 'out' && role?.name === 'USER' && user) {
      const userBalance = user.Balance?.amount || 0;
      const totalActiveBonus = user.totalActiveBonus || 0;
      const calculatedBalance = userBalance - totalActiveBonus;
      return calculatedBalance > 0 ? calculatedBalance : 0;
    }
    return null;
  }, [mode, role, user]);

  // Check if current user has wager access
  const canShowWager = React.useMemo(() => {
    if (mode !== 'in' || role?.name !== 'USER') return false;
    const currentUser = authService.getStoredUser();
    return currentUser?.isWagerEnabled || false;
  }, [mode, role]);

  const handlePreset = (val: number) =>
    setAmount((prev) => (prev === '' ? val : Number(prev) + val));
  const handleCashOutAll = () => {
    // For USER type players, use available balance (balance minus active bonus)
    // For other roles, use full balance
    const amountToSet =
      role?.name === 'USER' && user
        ? Math.max(
            0,
            (user.Balance?.amount || 0) - (user.totalActiveBonus || 0),
          )
        : balance;
    setAmount(amountToSet);
  };
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) setAmount(val === '' ? '' : Number(val));
  };

  const handleSave = async () => {
    if (typeof amount !== 'number' || amount <= 0) return;

    setLoading(true);
    setError(null);

    try {
      let response;

      if (mode === 'in') {
        response = await topUpBalance(userId, {
          amount,
          reason: comment || 'Manual top up',
          wagerEnabled: wager,
        });
      } else {
        response = await withdrawBalance(userId, {
          amount,
          reason: comment || 'Manual withdrawal',
        });
      }

      onSave(amount, wager, comment);
      onSuccess?.(response);
    } catch (err: any) {
      const errorMessage =
        getErrorMessage?.(err) ||
        err?.message ||
        `Failed to ${mode === 'in' ? 'top up' : 'withdraw'} balance`;
      setError(errorMessage);
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div className={styles.balance}>Balance: ${balance}</div>
      {/* Show available balance for cash out for USER type players */}
      {mode === 'out' && role?.name === 'USER' && availableBalance !== null && (
        <div
          style={{
            marginTop: '8px',
            fontSize: '16px',
            color: '#2d2e82',
            fontWeight: '500',
          }}
        >
          Available balance for cash out: ${availableBalance}
        </div>
      )}
      <div style={{ margin: '24px 0' }}>
        <div className={styles.iconCircle}>
          {mode === 'in' ? (
            <PlusIcon width={90} height={90} />
          ) : (
            <MinusIcon width={90} height={90} />
          )}
        </div>
        <div className={styles.formTitle}>
          Cash {mode === 'in' ? 'In' : 'Out'}
        </div>
        <div
          className={`${styles.presetRow} ${
            mode === 'in' && presetAmounts.length === 4 ? styles.presetGrid : ''
          } ${mode === 'out' ? styles.singleButton : ''}`}
        >
          {mode === 'in' ? (
            // Show preset amounts for cash in
            presetAmounts.map((amt) => (
              <button
                key={amt}
                className={styles.presetBtn}
                onClick={() => handlePreset(amt)}
                disabled={loading}
              >
                +{amt}
              </button>
            ))
          ) : (
            // Show "Cash Out All" button for cash out
            <div style={{width: '100%'}}>
            {isChashoutable && (
              <div style={{color: 'red', marginBottom: '8px'}}>Please tell the player to exit the game.</div>
            )}
            <button

              className={styles.presetBtn}
              onClick={handleCashOutAll}
              disabled={loading || isChashoutable}
            >
              Cash Out All
            </button>
            </div>
          )}
        </div>
        <input
          type="text"
          placeholder="Enter Amount"
          value={amount}
          onChange={handleInput}
          className={styles.amountInput}
          disabled={loading}
        />
        <input
          type="text"
          placeholder="Comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className={styles.amountInput}
          style={{ marginTop: '12px' }}
          disabled={loading}
        />
         {canShowWager && (
          <div className={styles.wagerRow}>
            <span className={styles.wagerLabel}>Wager</span>
            <label className={styles.wagerToggle}>
              <input
                type="checkbox"
                checked={wager}
                onChange={() => setWager((w) => !w)}
                className={styles.wagerCheckbox}
                disabled={loading}
              />
              <span className={styles.wagerSlider} />
              <span
                className={styles.wagerKnob}
                style={{ left: wager ? 22 : 2 }}
              />
            </label>
          </div>
        )} 
        {error && (
          <div className={styles.errorBox}>
            <span style={{ fontSize: 22, marginRight: 6, lineHeight: 1 }}>
              ❌
            </span>
            {error}
          </div>
        )}
        <div className={styles.actionRow}>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={
              typeof amount !== 'number' ||
              amount <= 0 ||
              loading ||
              (mode === 'out' &&
                role?.name === 'USER' &&
                availableBalance === 0)
            }
          >
            {loading ? 'Processing...' : 'Save'}
          </button>
          <button
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CashInOutForm;
